import {
    Box,
    Card,
    CardBody,
    CardHeader,
    Divider,
    Flex,
    Hide,
    Image,
    Spinner,
    Stack,
    Step,
    StepDescription,
    StepIcon,
    StepIndicator,
    StepNumber,
    Stepper,
    StepSeparator,
    StepStatus,
    StepTitle,
    Text,
    useSteps
} from "@chakra-ui/react";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useCustomToast} from "../../../components/CustomToast/CustomToast";
import React, {useEffect, useState} from "react";
import {steps} from "../../../utils/boat-utils";
import {CUSTOMER_LOGO} from "../../../constants/config";
import BoatAccidentForm from "./Form/BoatAccidentForm";
import BoatStatementForm from "./Form/BoatStatementForm";
import BoatConditionsForm from "./Form/BoatConditionsForm";
import BoatObservationsForm from "./Form/BoatObservationsForm";
import BoatImagesForm from "./Form/BoatImagesForm";
import {boatService} from "../../../services/boatService";
import {ROUTES} from "../../../constants/routes-path";

function BoatEdit() {
    const {id} = useParams();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const showToast = useCustomToast();

    const [currentBoat, setCurrentBoat] = useState(undefined)
    const [currentBoatData, setCurrentBoatData] = useState(undefined)

    const {activeStep, setActiveStep} = useSteps({
        index: 2,
        count: steps.length,
    });

    useEffect(() => {
        if (id) {
            fetchBoat(id);
        }
    }, [id]);

    const fetchBoat = async (id) => {
        try {
            const boatResponse = await boatService.find(id);
            setCurrentBoat(boatResponse);
            setCurrentBoatData(boatResponse.data);
        } catch (err) {
            showToast({title: t(err), status: "error"});
        }
    };

    useEffect(() => {
        if (activeStep === steps.length) {
            saveBoat(currentBoatData);
        }
    }, [activeStep]);

    const saveBoat = async (boatData) => {
        const lastStep = activeStep === steps.length;

        try {
            const status = lastStep ? 'FINALIZADO_SEM_DESENHO' : 'RASCUNHO';

            await boatService.update(id, {
                status,
                data: boatData,
            });

            showToast({title: t('boat.messages.success.save'), status: "success"});

            if (lastStep) {
                navigate(ROUTES.boatsDetailsPath.replace(":id", id));
            }

        } catch (error) {
            showToast({title: t(error), status: "error"});
        }
    }

    const handleOnSubmit = (values, saveDraft = false) => {
        const boatData = getCurrentBoatData(values);

        if (saveDraft) {
            saveBoat(boatData);
        } else {
            setCurrentBoatData(boatData);

            setActiveStep(step => step + 1);
        }
    }

    const handleBack = () => {
        setActiveStep(step => step - 1);
    }

    const getCurrentBoatData = (values) => {
        switch (activeStep) {
            case 2:
                return {...currentBoatData, acidente: values}
            case 3:
                return { ...currentBoatData, versoes: values.versoes }
            case 4:
                return { ...currentBoatData, condicoes: values }
            case 5:
                return { ...currentBoatData, observacoes: values }
            case 6:
                return { ...currentBoatData, ...values }
        }

        return currentBoatData;
    }

    const getStepContent = (step) => {
        if (!currentBoat?.boatNumber) {
            return <Flex justify="center"><Spinner/></Flex>;
        }

        switch (step) {
            case 2:
                return <BoatAccidentForm onSubmit={handleOnSubmit} values={currentBoatData.acidente} />
            case 3:
                return <BoatStatementForm onSubmit={handleOnSubmit} onBack={handleBack} values={currentBoatData.versoes} />
            case 4:
                return <BoatConditionsForm onSubmit={handleOnSubmit} onBack={handleBack} values={currentBoatData.condicoes} />
            case 5:
                return <BoatObservationsForm onSubmit={handleOnSubmit} onBack={handleBack} values={currentBoatData.observacoes} />
            case 6:
                return <BoatImagesForm onSubmit={handleOnSubmit} onBack={handleBack} />
            default:
                return <Flex justify="center"><Spinner/></Flex>
        }
    }

    const renderedStep = getStepContent(activeStep)

    return (
        <Flex direction="column" pt={{sm: "125px", lg: "75px"}} w="100%">
            <Card px="0px" pt="10px" mb="24px">
                <CardHeader pt="22px">
                    <Flex gap="8" justify="column" pt="20px" px="40px">
                        <Flex gap="4" justify="space-between" w="100%">
                            <Text fontSize="lg" fontWeight="bold">{t('boat.report.title')}</Text>
                            <Text fontSize="lg"
                                  fontWeight="bold">{t('boat.report.boat-number')} #{currentBoat?.boatNumber}</Text>
                        </Flex>
                    </Flex>
                    <Flex gap="8" justify="column" pt="20px" px="40px">
                        <Flex gap="4" justify="space-between" w="100%">
                            <Image
                                src={CUSTOMER_LOGO}
                                alt="Logo 2"
                                w="200px"
                                h="200px"
                                maxH="100px"
                                objectFit="contain"
                            />
                            <Text></Text>
                        </Flex>
                    </Flex>

                    <Flex gap="4" justify="center" py="20px">
                        <Divider borderColor="gray.300"/>
                    </Flex>

                    <Flex gap="1" justify="center" py="20px">
                        <Stepper index={activeStep} size='sm'>
                            {steps.map((step, index) => (
                                <Step key={index}>
                                    <StepIndicator>
                                        <StepStatus
                                            complete={<StepIcon/>}
                                            incomplete={<StepNumber/>}
                                            active={<StepNumber/>}
                                        />
                                    </StepIndicator>

                                    <Hide below="md">
                                        <Box>
                                            <StepTitle fontSize="sm" fontWeight="bold">{t(step.title)}</StepTitle>
                                            <StepDescription fontSize="sm">{t(step.subtitle)}</StepDescription>
                                        </Box>
                                    </Hide>

                                    <StepSeparator/>
                                </Step>
                            ))}
                        </Stepper>
                    </Flex>

                    <Flex gap="4" justify="center">
                        <Divider borderColor="gray.300"/>
                    </Flex>
                </CardHeader>
                <CardBody>
                    <Stack>
                        {renderedStep}
                    </Stack>
                </CardBody>
            </Card>
        </Flex>
    )
}

export default BoatEdit;